import React, { useState, useEffect, useRef } from "react";
import "./App.css";

const universities = [
  { id: "diu", name: "Daffodil International University", logo: "/logos/diu.png" },
  { id: "aiub", name: "American International University-Bangladesh", logo: "/logos/aiub.png" },
  { id: "brac", name: "BRAC University", logo: "/logos/brac.png" },
  { id: "nsu", name: "North South University", logo: "/logos/nsu.png" },
  { id: "ewu", name: "East West University", logo: "/logos/ewu.png" },
  { id: "uiu", name: "United International University", logo: "/logos/uiu.png" },
];

function App() {
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("user-dark-mode") === "true");
  const [selectedUniversity, setSelectedUniversity] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("uni") || null;
  });
  const [universityData, setUniversityData] = useState(() => {
    const saved = localStorage.getItem("all-university-courses");
    return saved ? JSON.parse(saved) : {};
  });

  const [cgpa, setCgpa] = useState(null);
  const [totalCredits, setTotalCredits] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const navigateToUniversity = (uniId) => {
    const url = new URL(window.location);
    if (uniId) {
      url.searchParams.set("uni", uniId);
    } else {
      url.searchParams.delete("uni");
    }
    window.history.pushState({}, "", url);
    setSelectedUniversity(uniId);
    setCgpa(null);
  };

  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      setSelectedUniversity(params.get("uni") || null);
      setCgpa(null);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const cursorGlowRef = useRef(null);

  useEffect(() => {
    const updateCursor = (e) => {
      if (cursorGlowRef.current) {
        cursorGlowRef.current.style.left = `${e.clientX}px`;
        cursorGlowRef.current.style.top = `${e.clientY}px`;
      }
    };
    window.addEventListener("mousemove", updateCursor);
    return () => window.removeEventListener("mousemove", updateCursor);
  }, []);

  const currentCourses = selectedUniversity && universityData[selectedUniversity] 
    ? universityData[selectedUniversity] : [{ credit: "", grade: "" }];

  /* --- AUTO-SCROLL LOGIC --- */
  useEffect(() => {
    if (selectedUniversity && currentCourses.length > 1) {
      // Small timeout to ensure the DOM has rendered the new row
      setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: "smooth"
        });
      }, 50);
    }
  }, [currentCourses.length, selectedUniversity]);

  useEffect(() => {
    document.body.className = darkMode ? "dark" : "";
    localStorage.setItem("user-dark-mode", darkMode);
  }, [darkMode]);

  // Dynamic page title for SEO – each university gets its own descriptive title
  useEffect(() => {
    if (selectedUniversity) {
      const uniName = universities.find(u => u.id === selectedUniversity)?.name || "";
      document.title = `${uniName} CGPA Calculator | BD CGPA Calculator`;
    } else {
      document.title = "BD CGPA Calculator | Fast & Accurate for DIU, BRAC, NSU, AIUB, EWU, UIU";
    }
  }, [selectedUniversity]);

  useEffect(() => {
    localStorage.setItem("all-university-courses", JSON.stringify(universityData));
  }, [universityData]);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);


  const gradeTables = {
    diu: { "A+": 4.0, "A": 3.75, "A-": 3.5, "B+": 3.25, "B": 3.0, "B-": 2.75, "C+": 2.5, "C": 2.25, "D": 2.0, "F": 0.0 },
    aiub: { "A+": 4.0, "A": 3.75, "B+": 3.5, "B": 3.25, "C+": 3.0, "C": 2.75, "D+": 2.5, "D": 2.25, "F": 0.0 },
    brac: { "A+": 4.0, "A": 4.0, "A-": 3.7, "B+": 3.3, "B": 3.0, "B-": 2.7, "C+": 2.3, "C": 2.0, "C-": 1.7, "D+": 1.3, "D": 1.0, "D-": 0.7, "F": 0.0 },
    nsu: { "A": 4.0, "A-": 3.7, "B+": 3.3, "B": 3.0, "B-": 2.7, "C+": 2.3, "C": 2.0, "C-": 1.7, "D+": 1.3, "D": 1.0, "F": 0.0 },
    ewu: { "A+": 4.0, "A": 3.75, "A-": 3.5, "B+": 3.25, "B": 3.0, "B-": 2.75, "C+": 2.5, "C": 2.25, "D": 2.0, "F": 0.0 },
    uiu: { "A": 4.0, "A-": 3.67, "B+": 3.33, "B": 3.0, "B-": 2.67, "C+": 2.33, "C": 2.0, "C-": 1.67, "D+": 1.33, "D": 1.0, "F": 0.0 },
  };

  const updateCourses = (newCourses) => setUniversityData({ ...universityData, [selectedUniversity]: newCourses });

  const calculateCGPA = () => {
    let credits = 0, points = 0;
    const gradePoints = gradeTables[selectedUniversity];
    currentCourses.forEach((c) => {
      const cr = parseFloat(c.credit);
      if (cr > 0 && c.grade) { credits += cr; points += cr * gradePoints[c.grade]; }
    });
    setTotalCredits(credits);
    return credits === 0 ? "0.00" : (points / credits).toFixed(2);
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <div className="app-root">
      <div className="space-layer-1"></div>
      <div className="space-layer-2"></div>
      <div className="space-layer-3"></div>
      <div className="cursor-glow" ref={cursorGlowRef}></div>

      {!selectedUniversity ? (
        <div className="university-page">
          <h1 className="university-title">BD CGPA Calculator</h1>
          <p className="university-subtitle">Select your university to calculate your CGPA</p>
          <button className="dark-toggle-btn inline-toggle" onClick={() => setDarkMode(!darkMode)} aria-label="Toggle dark mode">
            {darkMode ? "☀ Light Mode" : "🌙 Dark Mode"}
          </button>
          <div className="university-grid" role="list" aria-label="Select your university">
            {universities.map((uni, index) => (
              <div
                key={uni.id}
                className="university-card"
                role="listitem"
                tabIndex={0}
                aria-label={`Select ${uni.name}`}
                onClick={() => navigateToUniversity(uni.id)}
                onKeyDown={(e) => e.key === 'Enter' && navigateToUniversity(uni.id)}
              >
                <span className="uni-card-serial">{index + 1}.</span>
                <div className="university-logo-wrapper"><img src={uni.logo} alt={`${uni.name} logo`} className="university-logo" /></div>
                <div className="university-name">{uni.name}</div>
                <span className="uni-card-arrow" aria-hidden="true">›</span>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="App">
          {/* Prominent back button – always visible at the top */}
          <div className="calc-header">
            <button
              className="back-btn"
              onClick={() => navigateToUniversity(null)}
              aria-label="Back to university selection"
            >
              ← Back
            </button>
            <button className="dark-toggle-btn" onClick={() => setDarkMode(!darkMode)} aria-label="Toggle dark mode">
              {darkMode ? "☀ Light Mode" : "🌙 Dark Mode"}
            </button>
          </div>

          <div className="uni-badge">
            <img
              src={universities.find(u => u.id === selectedUniversity)?.logo}
              alt={universities.find(u => u.id === selectedUniversity)?.name}
              className="uni-badge-logo"
            />
            <span>{universities.find(u => u.id === selectedUniversity)?.name}</span>
          </div>

          <h1>CGPA Calculator</h1>
          <p className="subheading">Add your courses, select grades, and hit Calculate</p>

          <div className="course-container">
            {currentCourses.map((course, index) => (
              <div key={index} className="course-row">
                <span className="serial">{index + 1}.</span>
                <input type="number" placeholder="Credit" value={course.credit} onChange={(e) => {
                    const updated = [...currentCourses];
                    updated[index].credit = e.target.value;
                    updateCourses(updated);
                }} />
                <select value={course.grade} onChange={(e) => {
                    const updated = [...currentCourses];
                    updated[index].grade = e.target.value;
                    updateCourses(updated);
                }}>
                  <option value="">Select Grade</option>
                  {Object.entries(gradeTables[selectedUniversity]).map(([g, p]) => <option key={g} value={g}>{g} ({p.toFixed(2)})</option>)}
                </select>
                <button className="remove-btn" onClick={() => {
                  const updated = currentCourses.filter((_, i) => i !== index);
                  updateCourses(updated.length ? updated : [{ credit: "", grade: "" }]);
                }}>Remove</button>
              </div>
            ))}
          </div>

          {cgpa && (
            <div className="result-box">
              <h2 className="cgpa-display">CGPA: {cgpa}</h2>
              <p className="total-credit">Total Credits: {totalCredits}</p>
            </div>
          )}

          <div className="sticky-bar">
            <button className="add-btn" aria-label="Add a new course" onClick={() => updateCourses([...currentCourses, { credit: "", grade: "" }])}>+ Add Course</button>
            <button className="calculate-btn" aria-label="Calculate CGPA" onClick={() => setCgpa(calculateCGPA())}>Calculate CGPA</button>
            <button className="reset-btn" aria-label="Reset all courses" onClick={() => { updateCourses([{ credit: "", grade: "" }]); setCgpa(null); }}>Reset</button>
          </div>

          {showScrollTop && (
            <button className="scroll-top-btn" onClick={scrollToTop}>↑</button>
          )}

          <footer className="footer">© 2026 Md Nadim Mahmud. All rights reserved.</footer>
        </div>
      )}
    </div>
  );
}

export default App;